import { type ChangeEvent, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Divider, Stack, TextField as MuiTextField } from '@mui/material';

import {
  partnerPayoutsQuery,
  partnerPayoutsStatsQuery,
} from '@/entities/partner';
import { useAppQuery } from '@/shared/lib/useAppQuery';
import { Paper } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { CustomTypography } from '@/shared/ui/Typography';
import { dayjs } from '@/shared/utils/dayjs';

import { formatAffiliateAmount } from '../formatAmount';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
};

const startOfMonth = () => dayjs().startOf('month').format('YYYY-MM-DD');
const today = () => dayjs().format('YYYY-MM-DD');

export function PayoutsTab(): ReactNode {
  const { t } = useTranslation();
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());

  const { data: stats, isError: statsIsError } = useAppQuery({
    ...partnerPayoutsStatsQuery({ from, to }),
  });
  const { data: payouts, isPending, isError: payoutsIsError } = useAppQuery({
    ...partnerPayoutsQuery({ from, to, limit: 100, offset: 0 }),
  });

  const items = payouts?.items ?? [];
  const paymentsTotalMajor = statsIsError ? 0 : (stats?.paymentsTotalMinor ?? 0) / 100;
  const rewardTotalMajor = statsIsError ? 0 : (stats?.rewardTotalMinor ?? 0) / 100;
  const statsCurrency = statsIsError ? undefined : stats?.currency;

  if (payoutsIsError) {
    return <Alert severity="error">Не удалось загрузить выплаты</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: '20px' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <MuiTextField
              size="small"
              type="date"
              label={t('common:affiliatePage.payoutsTab.dateFrom')}
              value={from}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <MuiTextField
              size="small"
              type="date"
              label={t('common:affiliatePage.payoutsTab.dateTo')}
              value={to}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
            <Stack spacing={0.5}>
              <CustomTypography variant="caption" color="text.secondary">
                {t('common:affiliatePage.payoutsTab.paymentsForPeriod')}
              </CustomTypography>
              <CustomTypography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {formatAffiliateAmount(paymentsTotalMajor, statsCurrency)}
              </CustomTypography>
            </Stack>
            <Stack spacing={0.5}>
              <CustomTypography variant="caption" color="text.secondary">
                {t('common:affiliatePage.payoutsTab.rewardTotal')}
              </CustomTypography>
              <CustomTypography variant="h6" sx={{ fontWeight: 700, color: 'actions.go' }}>
                {formatAffiliateAmount(rewardTotalMajor, statsCurrency)}
              </CustomTypography>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 0, borderRadius: '20px', overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '140px 200px 1fr 160px 100px', gap: 1, px: 3, py: 1.5, opacity: 0.6 }}>
          <CustomTypography variant="caption">{t('common:affiliatePage.payoutsTab.cols.date')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.payoutsTab.cols.referral')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.payoutsTab.cols.payment')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.payoutsTab.cols.amount')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.payoutsTab.cols.percent')}</CustomTypography>
        </Box>
        <Divider />

        {isPending && !payouts ? (
          <Loader containerSize="medium" />
        ) : items.length === 0 ? (
          <NotFoundData
            title={t('common:affiliatePage.paymentsNotFoundTitle')}
            caption={t('common:affiliatePage.paymentsNotFoundCaption')}
          />
        ) : (
          items.map((p) => (
            <Box
              key={p.paymentId}
              sx={{
                display: 'grid',
                gridTemplateColumns: '140px 200px 1fr 160px 100px',
                gap: 1,
                px: 3,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'custom.black.10',
                alignItems: 'center',
              }}
            >
              <CustomTypography>{formatDate(p.paidAt)}</CustomTypography>
              <CustomTypography sx={{ fontFamily: 'monospace' }}>
                {p.referredEmail ?? p.referredAccountId.slice(0, 12)}
              </CustomTypography>
              <CustomTypography color="text.secondary">
                {p.paymentType}
              </CustomTypography>
              <CustomTypography>
                {formatAffiliateAmount(p.amountMinor / 100, p.currency)}
              </CustomTypography>
              <CustomTypography>{p.rewardPercent}%</CustomTypography>
            </Box>
          ))
        )}
      </Paper>
    </Stack>
  );
}
